// src/features/points/pointsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../app/axiosInstance";

/* ------------------------------------------------------------------ */
/* 💰 Thunks                                                          */
/* ------------------------------------------------------------------ */

// 1️⃣ GET /points/users/points
export const fetchUserPoints = createAsyncThunk(
  "points/fetchUserPoints",
  async ({ phone }, thunkAPI) => {
    try {
      const { data } = await axiosInstance.get("/points/users/points", {
        params: { phone },
      });
      return data.data; // -> { points, balance }
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.error ||
          err.response?.data?.errorMessage ||
          err.message
      );
    }
  }
);

// 2️⃣ POST /points/users/convert-points
export const convertPoints = createAsyncThunk(
  "points/convertPoints",
  async ({ phone, points }, thunkAPI) => {
    try {
      const { data } = await axiosInstance.post(
        "/points/users/convert-points",
        { phone, points }
      );
      return data.data; // -> { remainingPoints, balance }
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.error ||
          err.response?.data?.errorMessage ||
          err.message
      );
    }
  }
);

// 3️⃣ GET /points/users/point-conversions
export const fetchPointConversions = createAsyncThunk(
  "points/fetchPointConversions",
  async ({ phone, page = 1, limit = 10 }, thunkAPI) => {
    try {
      const { data } = await axiosInstance.get(
        "/points/users/point-conversions",
        { params: { phone, page, limit } }
      );
      return data; // whole { data, pagination }
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.error ||
          err.response?.data?.errorMessage ||
          err.message
      );
    }
  }
);

/* ------------------------------------------------------------------ */
/* 🧩 Slice                                                           */
/* ------------------------------------------------------------------ */
const pointsSlice = createSlice({
  name: "points",
  initialState: {
    points: 0,
    balance: 0,

    /** extra info about the very last conversion */
    lastConversion: null,

    conversions: [],          // latest page
    conversionsPagination: {},

    conversionStatus: null,   // success toast data

    loadingPoints: false,
    loadingConvert: false,
    loadingHistory: false,

    error: null,
  },
  reducers: {
    clearConversionStatus(state) {
      state.conversionStatus = null;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* fetchUserPoints */
      .addCase(fetchUserPoints.pending, (state) => {
        state.loadingPoints = true;
        state.error = null;
      })
      .addCase(fetchUserPoints.fulfilled, (state, { payload }) => {
        state.loadingPoints = false;
        state.points = payload.points;
        state.balance = payload.balance;
      })
      .addCase(fetchUserPoints.rejected, (state, { payload }) => {
        state.loadingPoints = false;
        state.error = payload || "Failed to fetch user points";
      })

      /* convertPoints */
      .addCase(convertPoints.pending, (state) => {
        state.loadingConvert = true;
        state.error = null;
        state.conversionStatus = null;
      })
      .addCase(convertPoints.fulfilled, (state, { payload }) => {
        state.loadingConvert = false;
        state.conversionStatus = "success";
        state.points = payload.remainingPoints;
        state.balance = payload.balance;
         // full record of this conversion
        state.lastConversion = { ...payload };

        // optimistic update into history (optional UX)
        state.conversions.unshift({
          ...payload,
          createdAt: Date.now(),
        });
      })
      .addCase(convertPoints.rejected, (state, { payload }) => {
        state.loadingConvert = false;
        state.error = payload || "Failed to convert points";
      })

      /* fetchPointConversions */
      .addCase(fetchPointConversions.pending, (state) => {
        state.loadingHistory = true;
        state.error = null;
      })
      .addCase(fetchPointConversions.fulfilled, (state, { payload }) => {
        state.loadingHistory = false;
        state.conversions = payload.data;
        state.conversionsPagination = payload.pagination;
      })
      .addCase(fetchPointConversions.rejected, (state, { payload }) => {
        state.loadingHistory = false;
        state.error = payload || "Failed to fetch point-conversion history";
      });
  },
});

/* ------------------------------------------------------------------ */
/* 🔎 Selectors                                                       */
/* ------------------------------------------------------------------ */
export const selectPoints            = (state) => state.points.points;
export const selectPointsBalance     = (state) => state.points.balance;

export const selectLastConversion    = (state) => state.points.lastConversion;

export const selectLoadingPoints     = (state) => state.points.loadingPoints;
export const selectLoadingConvert    = (state) => state.points.loadingConvert;
export const selectLoadingHistory    = (state) => state.points.loadingHistory;

export const selectPointsError       = (state) => state.points.error;
export const selectConversionStatus  = (state) => state.points.conversionStatus;

export const {
  clearConversionStatus,
  clearError,
} = pointsSlice.actions;

export default pointsSlice.reducer;


// // src/features/points/pointsSlice.js
// import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
// import axiosInstance from "../app/axiosInstance";

// /* -------------------------------------------------------------------------- */
// /* 💰  Thunks                                                                 */
// /* -------------------------------------------------------------------------- */

// // 1️⃣ GET /api/users/points  (axiosInstance baseURL already `/api`)
// export const fetchUserPoints = createAsyncThunk(
//   "points/fetchUserPoints",
//   async ({ phone }, thunkAPI) => {
//     try {
//       const res = await axiosInstance.get("/points/users/points", { params: { phone } });
//       return res.data; // { message, data: { points, balance } }
//     } catch (err) {
//       return thunkAPI.rejectWithValue(
//         err.response?.data?.errorMessage || err.message
//       );
//     }
//   }
// );

// // 2️⃣ POST /api/points/users/convert-points
// export const convertPoints = createAsyncThunk(
//   "points/convertPoints",
//   async ({ phone, points }, thunkAPI) => {
//     try {
//       const res = await axiosInstance.post(
//         "/points/users/convert-points",
//         { phone, points }
//       );
//       return res.data;
//     } catch (err) {
//       return thunkAPI.rejectWithValue(
//         err.response?.data?.errorMessage || err.message
//       );
//     }
//   }
// );

// // 3️⃣ GET /api/users/point-conversions
// export const fetchPointConversions = createAsyncThunk(
//   "points/fetchPointConversions",
//   async ({ phone, page = 1, limit = 10 }, thunkAPI) => {
//     try {
//       const res = await axiosInstance.get("/points/users/point-conversions", {
//         params: { phone, page, limit },
//       });
//       return res.data;
//     } catch (err) {
//       return thunkAPI.rejectWithValue(
//         err.response?.data?.errorMessage || err.message
//       );
//     }
//   }
// );

// /* -------------------------------------------------------------------------- */
// /* 🧩  Slice                                                                 */
// /* -------------------------------------------------------------------------- */
// const pointsSlice = createSlice({
//   name: "points",
//   initialState: {
//     points: 0,
//     balance: 0,
//     conversions: [],
//     conversionsPagination: {},
//     conversionStatus: null,
//     loading: false,
//     error: null,
//   },
//   reducers: {
//     clearConversionStatus(state) {
//       state.conversionStatus = null;
//     },
//     clearError(state) {
//       state.error = null;
//     },
//   },
//   extraReducers: (builder) => {
//     builder
//       /* fetchUserPoints */
//       .addCase(fetchUserPoints.pending, (state) => {
//         state.loading = true;
//         state.error = null;
//       })
//       .addCase(fetchUserPoints.fulfilled, (state, { payload }) => {
//         state.loading = false;
//         state.points = payload.data.points;
//         state.balance = payload.data.balance;
//       })
//       .addCase(fetchUserPoints.rejected, (state, { payload }) => {
//         state.loading = false;
//         state.error = payload || "Failed to fetch user points";
//       })

//       /* convertPoints */
//       .addCase(convertPoints.pending, (state) => {
//         state.loading = true;
//         state.error = null;
//         state.conversionStatus = null;
//       })
//       .addCase(convertPoints.fulfilled, (state, { payload }) => {
//         state.loading = false;
//         state.conversionStatus = payload;
//         state.points = payload.data.remainingPoints;
//         state.balance = payload.data.balance;
//       })
//       .addCase(convertPoints.rejected, (state, { payload }) => {
//         state.loading = false;
//         state.error = payload || "Failed to convert points";
//       })

//       /* fetchPointConversions */
//       .addCase(fetchPointConversions.pending, (state) => {
//         state.loading = true;
//         state.error = null;
//       })
//       .addCase(fetchPointConversions.fulfilled, (state, { payload }) => {
//         state.loading = false;
//         state.conversions = payload.data;
//         state.conversionsPagination = payload.pagination;
//       })
//       .addCase(fetchPointConversions.rejected, (state, { payload }) => {
//         state.loading = false;
//         state.error =
//           payload || "Failed to fetch point-conversion history";
//       });
//   },
// });

// /* -------------------------------------------------------------------------- */
// /* 🔎  Selectors                                                              */
// /* -------------------------------------------------------------------------- */
// export const selectPoints            = (state) => state.points.points;
// export const selectPointsBalance     = (state) => state.points.balance;
// export const selectPointsLoading     = (state) => state.points.loading;
// export const selectPointsError       = (state) => state.points.error;
// export const selectConversionStatus  = (state) => state.points.conversionStatus;

// export const { clearConversionStatus, clearError } = pointsSlice.actions;
// export default pointsSlice.reducer;
